import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { FaEtsy } from 'react-icons/fa';
import { MapPin, Mail, Heart } from 'lucide-react';
import { getPath } from '../config/routeMap';

const Footer = () => {
  const { t, language } = useLanguage();

  const quickLinks = [
    { key: 'home',       to: getPath('home', language),       label: t('nav.home') },
    { key: 'products',   to: getPath('products', language),   label: t('nav.products') },
    { key: 'story',      to: getPath('story', language),      label: t('nav.story') },
    { key: 'healthcare', to: getPath('healthcare', language), label: t('nav.healthcare') },
    { key: 'faq',        to: getPath('faq', language),        label: 'FAQ' },
    { key: 'guides',     to: getPath('guides', language),     label: 'Guides' },
    { key: 'contact',    to: getPath('contact', language),    label: t('nav.contact') },
  ];

  const homeLabel = language === 'fr' ? 'MyGuardPod \u2014 Accueil' : 'MyGuardPod \u2014 Home';

  return (
    <footer className="bg-[#1a1a1a] text-white">
      {/* Main CTA Section */}
      <div className="border-b border-neutral-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20 text-center">
          <h2 className="font-outfit font-black text-3xl sm:text-4xl lg:text-5xl tracking-tight mb-4">
            {t('cta.title')}
          </h2>
          <p className="font-manrope text-neutral-400 max-w-2xl mx-auto mb-8 text-lg">
            {t('cta.description')}
          </p>
          <a
            href="https://www.etsy.com/shop/MyGuardPod"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center space-x-3 bg-[#E31B23] hover:bg-[#c8171e] text-white px-8 py-4 rounded-full font-bold text-lg transition-all duration-300 active:scale-95"
            data-testid="footer-etsy-cta"
          >
            <FaEtsy className="w-6 h-6" />
            <span>{t('cta.button')}</span>
          </a>
        </div>
      </div>

      {/* Footer Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-12">
          {/* Brand */}
          <div className="lg:col-span-2">
            <div className="mb-4">
              <Link to={getPath('home', language)} aria-label={homeLabel}>
                <img
                  src="/images/LOGO_MGP.webp"
                  alt={homeLabel}
                  className="h-10 sm:h-12 w-auto"
                  width="240"
                  height="60"
                />
              </Link>
            </div>
            <p className="font-outfit font-bold text-lg text-[#E31B23] mb-2">
              {t('footer.tagline')}
            </p>
            <p className="font-manrope text-neutral-400 mb-6 max-w-md">
              {t('footer.description')}
            </p>
            <div className="flex flex-col space-y-2 text-neutral-400">
              <div className="flex items-center space-x-2">
                <MapPin className="w-4 h-4 text-[#E31B23]" />
                <span className="text-sm">Baie-Comeau, Québec, Canada</span>
              </div>
              <div className="flex items-center space-x-2">
                <Mail className="w-4 h-4 text-[#E31B23]" />
                <span className="text-sm">contact@myguardpod.com</span>
              </div>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="font-outfit font-bold text-sm uppercase tracking-widest text-[#E31B23] mb-4">
              {t('footer.quickLinks')}
            </h3>
            <ul className="space-y-3">
              {quickLinks.map((link) => (
                <li key={link.key}>
                  <Link
                    to={link.to}
                    className="font-manrope text-neutral-400 hover:text-white transition-colors"
                    data-testid={`footer-link-${link.key}`}
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Legal */}
          <div>
            <h3 className="font-outfit font-bold text-sm uppercase tracking-widest text-[#E31B23] mb-4">
              {t('footer.legal')}
            </h3>
            <ul className="space-y-3">
              <li>
                <a
                  href="https://www.etsy.com/shop/MyGuardPod"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-manrope text-neutral-400 hover:text-white transition-colors flex items-center space-x-2"
                >
                  <FaEtsy className="w-4 h-4" />
                  <span>Etsy Shop</span>
                </a>
              </li>
              <li>
                <Link to={getPath('privacy', language)} className="font-manrope text-neutral-400 hover:text-white transition-colors">
                  {t('footer.privacy')}
                </Link>
              </li>
              <li>
                <Link to={getPath('terms', language)} className="font-manrope text-neutral-400 hover:text-white transition-colors">
                  {t('footer.terms')}
                </Link>
              </li>
              <li>
                <button
                  type="button"
                  onClick={() => window.__openPrivacyPreferences && window.__openPrivacyPreferences()}
                  className="font-manrope text-neutral-400 hover:text-white transition-colors text-left"
                  data-testid="footer-privacy-preferences"
                >
                  {language === 'fr' ? 'Pr\u00e9f\u00e9rences de confidentialit\u00e9' : 'Privacy preferences'}
                </button>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="mt-12 pt-8 border-t border-neutral-800 flex flex-col gap-4">
          <p className="font-manrope text-xs text-neutral-500 leading-relaxed">
            {language === 'fr'
              ? 'Les noms de m\u00e9dicaments et de dispositifs sont des marques appartenant \u00e0 leurs propri\u00e9taires respectifs. Leur mention indique uniquement la compatibilit\u00e9 pr\u00e9vue et n\u2019implique aucune affiliation, approbation ou recommandation.'
              : 'Medication and device names are trademarks of their respective owners. References indicate intended fit only and do not imply affiliation, approval or endorsement.'}
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <p className="font-manrope text-sm text-neutral-500">
              {t('footer.copyright')}
            </p>
            <p className="font-manrope text-sm text-neutral-500 flex items-center gap-1">
              <Heart className="w-4 h-4 text-[#E31B23]" />
              {t('footer.madeIn')}
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
